/**
 * Sample answers for the planning join.
 *
 * The mock has to carry all THREE outcomes, not a happy path. The module
 * exists to keep "no window" and "cannot be assessed" apart, and a mock that
 * only ever returns a window would let a screen ship that draws the other two
 * identically — which is the exact failure the platform's own comments record
 * a console making.
 *
 * So the position asked about decides which case comes back, and the three
 * are reachable from the screen without a backend:
 *
 *   any stand on A   a window, with the verdict sentence
 *   any stand on B   both halves checked, neither clear — a real "no"
 *   any stand on C   both halves clear, no overlap — the case worth naming
 *   anything else    the traffic half unassessable — a plan with a hole in it
 */
import type { PlanAnswer } from "../domain/planning";
import type { PlanQuery as Query } from "../api/dataSource";

const at = (dayOffset: number, hour: number, minutes = 0): string => {
  const d = new Date();
  d.setUTCDate(d.getUTCDate() + dayOffset);
  d.setUTCHours(hour, minutes, 0, 0);
  return d.toISOString();
};

const base = (q: Query) => ({
  position: q.positionCode,
  category: q.category,
  hoursNeeded: q.hoursNeeded,
});

export function mockWhenToWork(q: Query): PlanAnswer {
  const stand = q.positionCode.trim().toUpperCase();

  /* ── a window, and the sentence a planner acts on ───────────────────── */
  if (stand.startsWith("A")) {
    const windows = [
      { from: at(1, 2), to: at(1, 6) },
      { from: at(2, 3), to: at(2, 7) },
    ];
    return {
      ...base(q),
      traffic: {
        found: true,
        known: true,
        windows: [
          { from: at(1, 1), to: at(1, 6) },
          { from: at(2, 0, 30), to: at(2, 7) },
        ],
      },
      weather: {
        found: true,
        known: true,
        windows: [
          { from: at(1, 2), to: at(1, 9) },
          { from: at(2, 3), to: at(2, 7) },
        ],
        assessedFrom: ["observation to 06:00", "forecast issued 18 h ahead"],
      },
      found: true,
      known: true,
      windows,
      windowCount: windows.length,
      earliest: windows[0],
      verdict: `do it ${windows[0].from} to ${windows[0].to} — clear of traffic and within the weather limits for '${q.category}'.`,
      caveat:
        "Traffic is tested against the PLAN and weather partly against a forecast. Confirm both on the day before committing a crew.",
    };
  }

  /* ── a real no: both halves checked, both blocked ───────────────────── */
  if (stand.startsWith("B")) {
    return {
      ...base(q),
      traffic: {
        found: false,
        known: true,
        windows: [],
        whyNot: "every window in this period leaves at least one movement with nowhere to go",
      },
      weather: {
        found: false,
        known: true,
        windows: [],
        whyNot: `no ${q.hoursNeeded} h window in this period is clear of the limits on file`,
        blocking: [
          ["surface temperature above 50 °C", 22],
          ["wind above 25 kt", 6],
        ],
      },
      found: false,
      known: true,
      whyNot: "no window in this period is clear of traffic and weather.",
      windows: [],
      weatherBlockers: [
        ["surface temperature above 50 °C", 22],
        ["wind above 25 kt", 6],
      ],
    };
  }

  /* ── the case worth naming: both clear, none overlapping ────────────── */
  if (stand.startsWith("C")) {
    return {
      ...base(q),
      traffic: {
        found: true,
        known: true,
        windows: [{ from: at(1, 22), to: at(2, 4) }],
      },
      weather: {
        found: true,
        known: true,
        windows: [{ from: at(1, 6), to: at(1, 14) }],
        assessedFrom: ["observation to 06:00"],
      },
      found: false,
      known: true,
      whyNot:
        `both constraints have clear windows, but none of them overlap for the ${q.hoursNeeded} h the job needs. ` +
        "This is the case worth knowing about: neither side is at fault and the job still cannot be done in this period.",
      windows: [],
    };
  }

  /* ── a plan with a hole in it ───────────────────────────────────────── */
  return {
    ...base(q),
    traffic: {
      found: false,
      known: false,
      windows: [],
      whyNot:
        "the schedule does not cover the whole search period. Any window this found would be clear only because nobody has loaded the flights for it.",
    },
    weather: {
      found: true,
      known: true,
      windows: [{ from: at(1, 2), to: at(1, 9) }],
      assessedFrom: ["observation to 06:00"],
    },
    found: false,
    known: false,
    whyNot:
      "the traffic side could not be assessed, so this is not a plan — it is a plan with a hole in it. " +
      "A window clear on one constraint and unchecked on the other looks exactly like a window clear on both.",
    windows: [],
  };
}
